import { useMutation, useQuery } from '@apollo/client';
import { fade, makeStyles } from '@material-ui/core';
import { useEffect, useState } from 'react';
import CompanyQuery from 'services/graphQL/query/setting/CompanyQuery';
import CompanySettingCategory, { CompanySettingkey } from 'utils/enum/CompanySettingEnum';
import ButtonStyles from 'styles/theme/Button';
import { FetchPolicy } from 'utils/enum/Core';
import ModalUtils from 'utils/ModalUtils';
import CompanyMutation from 'services/graphQL/mutate/CompanyMutation';
import ArrayUtils from 'lib/ArrayUtils';

const buttonStyles = makeStyles((theme) => ButtonStyles.getStyle(theme, fade));
const useStyles = makeStyles((theme) => ({
    box: {
        marginTop: '10px',
        paddingTop: '10px',
        border: `solid 1px ${theme.palette.border.mercury}`,
        height: 'auto !important',
        padding: '16px',
    },
    title: {
        width: '100%',
        paddingBottom: '8px',
        borderBottom: `solid 1px ${theme.palette.border.mercury}`,
        marginBottom: '32px',
        fontWeight: 600,
    },
    switchTitle: {
        fontWeight: 600,
    },
    switchSubTitle: {
        fontSize: '14px',
        color: theme.palette.text.secondary,
    },
}));

export default function useNotificationSettings() {
    const classes = { ...useStyles(), ...buttonStyles() };
    const [paymentValues, setPaymentValues] = useState({
        valueForAutoPay: false,
        valueForElectronicPayment: false,
        isPNM: false,
        hasProvider: false,
    });

    const {
        data: companySettingsData,
        loading: loadingCompanySettings,
        error: companySettingsError,
    } = useQuery(CompanyQuery.GET_COMPANY_SETTINGS, {
        variables: {
            category: CompanySettingCategory.ELECTRONIC_PAYMENT,
            key: [
                CompanySettingkey.SEND_EMAIL_RECEIPTS_FOR_AUTO_PAYMENTS,
                CompanySettingkey.SEND_EMAIL_RECEIPTS_FOR_EXTERNAL_ELECTRONIC_PAYMENTS,
                CompanySettingkey.PROVIDER,
            ],
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const [saveCompanySettings, { loading: isSavingCompanySettings }] = useMutation(CompanyMutation.SAVE_COMPANY_SETTINGS, {
        onCompleted: (response) => {
            if (response) {
                ModalUtils.successMessage(null, 'Settings updated successfully');
            }
        },
        onError: (errorMessage) => {
            ModalUtils.errorMessage([errorMessage]);
        },
    });

    useEffect(() => {
        if (companySettingsError) {
            ModalUtils.errorMessage(companySettingsError?.graphQLErrors);
            return;
        }

        if (!loadingCompanySettings && companySettingsData) {
            const { getCompanySettings: companySettings } = companySettingsData;
            if (ArrayUtils.isNotEmpty(companySettings)) {
                const autoPayment = companySettings.find(
                    (setting) => setting.key
              === CompanySettingkey.SEND_EMAIL_RECEIPTS_FOR_AUTO_PAYMENTS,
                )?.value ?? false;
                const electronicPayment = companySettings.find(
                    (setting) => setting.key
              === CompanySettingkey.SEND_EMAIL_RECEIPTS_FOR_EXTERNAL_ELECTRONIC_PAYMENTS,
                )?.value ?? false;
                const provider = companySettings.find(
                    (setting) => setting.key === CompanySettingkey.PROVIDER,
                )?.value ?? '';
                const isPNMValue = provider === 'PayNearMe';

                setPaymentValues({
                    isPNM: isPNMValue,
                    valueForAutoPay: autoPayment,
                    valueForElectronicPayment: electronicPayment,
                    hasProvider: !!provider,
                });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companySettingsData, companySettingsError]);

    const autoPaymentValue = paymentValues.valueForAutoPay?.toString()?.toLowerCase() === 'true' ?? false;
    const electronicPaymentValue = paymentValues.valueForElectronicPayment?.toString()?.toLowerCase() === 'true' ?? false;

    const toggleAllowVehiclesAvailableLots = (event, settingKey) => {
        const newValue = event.target.checked.toString();
        const isSettingForAutoPay = settingKey === CompanySettingkey.SEND_EMAIL_RECEIPTS_FOR_AUTO_PAYMENTS;

        saveCompanySettings({
            variables: {
                category: CompanySettingCategory.ELECTRONIC_PAYMENT,
                key: settingKey,
                value: newValue,
            },
        });

        if (isSettingForAutoPay) {
            setPaymentValues((prevState) => ({
                ...prevState,
                valueForAutoPay: newValue,
            }));
            return;
        }

        setPaymentValues((prevState) => ({
            ...prevState,
            valueForElectronicPayment: newValue,
        }));
    };

    const disabledSwitch = loadingCompanySettings || isSavingCompanySettings;

    return {
        classes,
        disabledSwitch,
        autoPaymentValue,
        electronicPaymentValue,
        isPNM: paymentValues.isPNM,
        hasProvider: paymentValues.hasProvider,

        toggleAllowVehiclesAvailableLots,
    };
}
